// @ts-nocheck
/**
 * Sidebar constants, data, access rules, and icon components.
 * Extracted from the original Sidebar.js for cleaner code organization.
 * Type checking disabled for incremental migration; types to be added in a follow-up PR.
 */
import React from 'react';
import {
  Home, BarChart2, Grid, Plus,
  LayoutDashboard, FileText, ArrowLeftRight,
  PieChart, Receipt, Percent, RefreshCw,
  BarChart3, ScanLine, Link2, Users, UserCheck,
  TrendingUp, CreditCard, BookOpen,
  GitCompare, Search, Users2, Package,
  Building2, Key, Wallet, GitMerge,
  Settings as SettingsIcon, HelpCircle,
  MoreHorizontal, Bookmark,
  Keyboard, ClipboardList, MessageSquare, Zap,
  Sliders,
} from 'lucide-react';

// === Colors + typography ===
export const ACCENT  = '#15A08C';
export const FONT    = "'Inter', -apple-system, sans-serif";
export const DEFAULT = '#0F172A';
export const MUTED   = '#334155';

// === Animations ===
export const SLIDE_IN = `
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-12px) }
    to   { opacity: 1; transform: translateX(0) }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.97) }
    to   { opacity: 1; transform: scale(1) }
  }
`;

// === Icon components ===

export function AsteriskIcon({ size, color }) {
  const s  = size || 22;
  const cx = s / 2;
  const r  = s / 2 - 2;
  const lines = Array.from({ length: 8 }, function(_, i) {
    const angle = (i * 45 * Math.PI) / 180;
    return { x1: cx, y1: cx, x2: cx + r * Math.cos(angle), y2: cx + r * Math.sin(angle) };
  });
  return (
    <svg width={s} height={s} viewBox={'0 0 ' + s + ' ' + s} fill="none">
      {lines.map(function(l, i) {
        return <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke={color || DEFAULT} strokeWidth="2.5" strokeLinecap="round"/>;
      })}
    </svg>
  );
}

// 2-line sliders icon with knobs
export function CustomSlidersIcon({ size, color }) {
  const sz = size || 20;
  const stroke = color || DEFAULT;
  return (
    <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none">
      <line x1="4" y1="7" x2="20" y2="7" stroke={stroke} strokeWidth="2" strokeLinecap="round"/>
      <circle cx="9" cy="7" r="2.5" fill={stroke}/>
      <line x1="4" y1="17" x2="20" y2="17" stroke={stroke} strokeWidth="2" strokeLinecap="round"/>
      <circle cx="15" cy="17" r="2.5" fill={stroke}/>
    </svg>
  );
}

// === Role-based access control ===

export function isAppVisibleForRole(appId, role) {
  if (!role || role === 'owner' || role === 'admin' || role === 'accountant') return true;
  if (role === 'staff') {
    return ['home','accounting','feed','bookmarks','more'].includes(appId);
  }
  if (role === 'viewer') {
    return !['payroll','team','marketing','hr','customize','create','allapps'].includes(appId);
  }
  return true;
}

export function isCreateItemVisibleForRole(path, role) {
  if (!role || role === 'owner' || role === 'admin' || role === 'accountant') return true;
  return false;
}

// === App catalog ===
// NOTE: app `id` must match a case in FlyServiceIcon (Flyouts.tsx) for the circled icon
// to render in flyouts. Supported ids: expenses, accounting, sales, customer, payroll,
// team, tax, marketing, hr.

export const ALL_APPS = [
  {
    id: 'accounting', label: 'Accounting', color: '#15A08C', icon: LayoutDashboard,
    items: [
      { label: 'Accounting overview',      path: '/accounting'                                 },
      { label: 'Bank transactions',        path: '/accounting/bank-transactions'               },
      { label: 'Integration transactions', path: '/accounting/integration-transactions'        },
      { label: 'Receipts',                 path: '/accounting/receipts'                        },
      { label: 'Reconcile',                path: '/accounting/reconcile'                       },
      { label: 'Rules',                    path: '/accounting/rules'                           },
      { label: 'Chart of accounts',        path: '/accounting/chart-of-accounts'               },
      { label: 'Recurring transactions',   path: '/accounting/recurring'                       },
      { label: 'My accountant',            path: '/accounting/my-accountant'                   },
    ],
  },
  {
    id: 'expenses', label: 'Expenses & Bills', color: '#EF4444', icon: Receipt,
    items: [
      { label: 'Expenses',        path: '/transactions'             },
      { label: 'Bills',           path: '/billpay'                  },
      { label: 'Vendors',         path: '/vendors'                  },
      { label: 'Purchase orders', path: '/expenses/purchase-orders' },
    ],
  },
  {
    // Sales items match the real Sales routes in src/routes/authedRoutes.tsx
    // so every row in the AllAppsLayout panel resolves to a real page.
    id: 'sales', label: 'Sales & Get Paid', color: '#F59E0B', icon: TrendingUp,
    items: [
      { label: 'Overview',            path: '/invoices'                 },
      { label: 'Invoices',            path: '/invoices/list'            },
      { label: 'Payment links',       path: '/invoices/payment-links'   },
      { label: 'Payouts',             path: '/invoices/payouts'         },
      { label: 'Products & services', path: '/invoices/products'        },
      { label: 'Chase hub',           path: '/invoices/chase'           },
    ],
  },
  {
    id: 'customer', label: 'Customer Hub', color: '#06B6D4', icon: Users2,
    items: [
      { label: 'Customers',           path: '/customers'            },
      { label: 'Products & services', path: '/inventory'            },
      { label: 'Statements',          path: '/customers/statements' },
    ],
  },
  {
    id: 'payroll', label: 'Payroll', color: '#3B82F6', icon: Users,
    items: [
      { label: 'Overview',      path: '/payroll/overview'   },
      { label: 'Pay runs',      path: '/payroll/drafts'     },
      { label: 'Pay schedules',   path: '/payroll/schedules'  },
      { label: 'Employees',     path: '/payroll/employees'  },
      { label: 'Paycheque list', path: '/payroll/paycheques' },
      { label: 'Payroll taxes', path: '/payroll/taxes'      },
      { label: 'Compliance',    path: '/payroll/compliance' },
    ],
  },
  {
    id: 'team', label: 'Team', color: '#6366F1', icon: Users,
    items: [
      { label: 'Team members',        path: '/team'            },
      { label: 'Timesheets',          path: '/team/timesheets' },
      { label: 'Roles & permissions', path: '/team/roles'      },
    ],
  },
  {
    id: 'tax', label: 'Sales Tax', color: '#F59E0B', icon: Percent,
    items: [
      { label: 'Sales tax overview', path: '/tax'         },
      { label: 'Tax rates',          path: '/tax/rates'   },
      { label: 'Tax filings',        path: '/tax/filings' },
    ],
  },
  {
    id: 'marketing', label: 'Marketing', color: '#F97316', icon: Zap, premium: true,
    items: [
      { label: 'Campaigns',       path: '/marketing/campaigns' },
      { label: 'Email templates', path: '/marketing/templates' },
      { label: 'Audience',        path: '/marketing/audience'  },
    ],
  },
  {
    id: 'hr', label: 'HR', color: '#10B981', icon: UserCheck,
    items: [
      { label: 'Overview',  path: '/hr'           },
      { label: 'Employees', path: '/hr/employees' },
      { label: 'Hiring',    path: '/hr/hiring'    },
      { label: 'Documents', path: '/hr/documents' },
      { label: 'Time off',  path: '/hr/time-off'  },
      { label: 'Reviews',   path: '/hr/reviews'   },
    ],
  },
];

export const APP_GROUPS = [
  {
    label: 'Core', color: '#15A08C',
    items: [
      { label: 'Dashboard',    path: '/',             icon: LayoutDashboard },
      { label: 'Documents',    path: '/documents',    icon: FileText        },
      { label: 'Transactions', path: '/transactions', icon: ArrowLeftRight  },
    ],
  },
  {
    label: 'Finance', color: '#3B82F6',
    items: [
      { label: 'Reports',        path: '/reports',        icon: BarChart2  },
      { label: 'Reconciliation', path: '/accounting/reconcile', icon: GitMerge   },
      { label: 'Ledger View',    path: '/ledger',         icon: BookOpen   },
      { label: 'Variance',       path: '/variance',       icon: TrendingUp },
      { label: 'Bill Pay',       path: '/billpay',        icon: Wallet     },
      { label: 'Budgets',        path: '/budgets',        icon: PieChart   },
      { label: 'Invoices',       path: '/invoices',       icon: Receipt    },
      { label: 'Tax',            path: '/tax',            icon: Percent    },
      { label: 'Billing',        path: '/billing',        icon: CreditCard },
      { label: 'Currency',       path: '/currency',       icon: RefreshCw  },
    ],
  },
  {
    label: 'Business', color: '#8B5CF6',
    items: [
      { label: 'Customers',    path: '/customers',    icon: Users2    },
      { label: 'Inventory',    path: '/inventory',    icon: Package   },
      { label: 'Businesses',   path: '/businesses',   icon: Building2 },
      { label: 'Vendors',      path: '/vendors',      icon: BarChart3 },
      { label: 'Team',         path: '/team',         icon: Users     },
      { label: 'HR',           path: '/hr',           icon: UserCheck },
      { label: 'Integrations', path: '/integrations', icon: Link2     },
    ],
  },
  {
    label: 'Tools', color: '#F59E0B',
    items: [
      { label: 'Smart Search', path: '/search',     icon: Search     },
      { label: 'Scanner',      path: '/accounting/receipts',   icon: ScanLine   },
      { label: 'Doc Compare',  path: '/comparison', icon: GitCompare },
      { label: 'API Access',   path: '/api-access', icon: Key        },
    ],
  },
];

export const CREATE_COLUMNS = [
  {
    header: 'Customers',
    items: [
      { label: 'Invoice',          path: '/invoices'              },
      { label: 'Payment links',    path: '/create/payment-links'  },
      { label: 'Receive payment',  path: '/transactions'          },
      { label: 'Statement',        path: '/create/statement'      },
      { label: 'Estimate',         path: '/invoices'              },
      { label: 'Credit memo',      path: '/create/credit-memo'    },
      { label: 'Sales receipt',    path: '/invoices'              },
      { label: 'Refund receipt',   path: '/create/refund-receipt' },
      { label: 'Add customer',     path: '/customers'             },
    ],
  },
  {
    header: 'Suppliers',
    items: [
      { label: 'Expense',            path: '/transactions'               },
      { label: 'Cheque',             path: '/create/cheque'              },
      { label: 'Bill',               path: '/billpay'                    },
      { label: 'Pay bills',          path: '/billpay'                    },
      { label: 'Purchase order',     path: '/create/purchase-order', premium: true },
      { label: 'Supplier credit',    path: '/create/supplier-credit'    },
      { label: 'Credit card credit', path: '/create/credit-card-credit' },
      { label: 'Print cheques',      path: '/create/print-cheques'      },
      { label: 'Add supplier',       path: '/vendors'                   },
    ],
  },
  {
    header: 'Team',
    items: [
      { label: 'Weekly timesheet', path: '/team/timesheets', premium: true },
    ],
  },
  {
    header: 'Business',
    items: [
      { label: 'Upload Document', path: '/documents' },
      { label: 'Scan Receipt',    path: '/accounting/receipts'  },
      { label: 'New Budget',      path: '/budgets'   },
    ],
  },
  {
    header: 'Other',
    items: [
      { label: 'Task',                  path: '/create/task'                },
      { label: 'Bank deposit',          path: '/transactions'               },
      { label: 'Transfer',              path: '/create/transfer'            },
      { label: 'Journal entry',         path: '/ledger'                     },
      { label: 'Pay down credit card',  path: '/create/pay-down-credit-card'},
      { label: 'Add product/service',   path: '/create/product-service'    },
      { label: 'Smart Search',          path: '/search'                     },
      { label: 'New chat', path: null, isChat: true                         },
    ],
  },
];

// REPORTS_ITEMS reordered: Financial planning first (most-used, forward-looking)
// Financial planning expands to its own sub-items on hover.
export const REPORTS_ITEMS = [
  {
    label: 'Financial planning',
    children: [
      { label: 'Cash flow overview', path: '/reports/planning/cash-flow-overview' },
      { label: 'Cash flow planner',  path: '/reports/planning/cash-flow-planner'  },
      { label: 'Budgets',            path: '/reports/planning/budgets'            },
    ],
  },
  { label: 'Management reports', path: '/reports/management' },
  { label: 'Standard reports',   path: '/reports' },
  { label: 'Custom reports',     path: '/reports/custom' },
];

export const SLIM_ITEMS = [
  { id: 'create',    icon: Plus,      label: 'Create',    flyout: 'create'    },
  { id: 'bookmarks', icon: Bookmark,  label: 'Bookmarks', flyout: 'bookmarks' },
  { id: 'home',      icon: Home,      label: 'Home',      path: '/'           },
  { id: 'feed',      icon: null,      label: 'Feed',      flyout: 'feed'      },
  { id: 'reports',   icon: BarChart2, label: 'Reports',   flyout: 'reports'   },
  { id: 'allapps',   icon: Grid,      label: 'All Apps',  flyout: 'apps'      },
];

export const SLIM_BOTTOM = [
  { id: 'more',      icon: MoreHorizontal, label: 'More',      flyout: 'more'      },
  { id: 'customize', icon: Sliders,        label: 'Customize', action: 'customize' },
];

export const MORE_ITEMS = [
  { label: 'Audit Log',          icon: ClipboardList, path: '/tools/audit-log'   },
  { label: 'Settings',           icon: SettingsIcon,  path: '/settings'          },
  { label: 'Help & Support',     icon: HelpCircle,    path: '/help'              },
  { label: 'Keyboard Shortcuts', icon: Keyboard,      path: null                 },
  { label: "What's New",         icon: Zap,           path: '/profile/whats-new' },
  { label: 'Send Feedback',      icon: MessageSquare, path: '/profile/feedback'  },
];

export const DEFAULT_APP_ORDER = [
  'accounting', 'expenses', 'sales', 'customer', 'payroll', 'team', 'tax', 'marketing', 'hr',
];